import type {IInputItem} from '../types/IInputItem';
import type {IBookingInfoItem} from '../types/IBookingInfoItem';
import type {TranslationFunction} from 'src/types/language-support.ts';
import type {IBookingMachineSnapshot} from '../machines/bookingMachine';

import capitalize from 'lodash/capitalize';
import {parse, formatISO, addDays} from 'date-fns';

import {
  getWeekDates,
  canNavigateNextWeek,
  getMeetLinkProviders,
  getLocationTitleLabelMap,
  canNavigatePreviousWeek,
} from '../utils/booking';

import {format, timezoneName} from '../utils/date';
import {TZDate} from '@date-fns/tz';
import type {IEventQuestion} from '../types/IEventQuestion';


export const selectBookingInfo = (
  state: IBookingMachineSnapshot,
  t: TranslationFunction,
  language: string,
  makeKey: (key: string) => string,
) => {
  const meetLinkProviders = getMeetLinkProviders(t);
  const locationTitleLabelMap = getLocationTitleLabelMap(t);
  const bookingInfoItems: IBookingInfoItem[] = [];
  const isRescheduleFlow = Boolean(state.context.eventDetails?.id);

  if (!state.matches('bookingForm') && !isRescheduleFlow) {
    const durationUnit = state.context.eventTemplate?.duration.unit;
    const durationAmount = state.context.eventTemplate?.duration.amount;

    if (durationUnit && durationAmount) {
      bookingInfoItems.push({
        label: `${durationAmount} ${t(
          makeKey(capitalize(durationUnit)),
          capitalize(durationUnit),
        )} ${t('Meeting', 'Meeting')}`,
        icon: 'clock',
      });
    }
  }

  if (
    isRescheduleFlow &&
    state.context.eventDetails?.event_end &&
    state.context.eventDetails?.event_start
  ) {
    const startDate = new TZDate(state.context.eventDetails?.event_start);
    const endDate = new TZDate(state.context.eventDetails?.event_end);

    bookingInfoItems.push({
      label: format(startDate, 'PP', {language}),
      icon: 'calendar',
    });

    const selectDateWithTimezone = timezoneName(
      state.context.eventDetails?.event_start,
      state.context.language,
    );

    bookingInfoItems.push({
      label: `${format(startDate, 'p', {language})} - ${format(endDate, 'p', {
        language,
      })}`,
      subLabel: selectDateWithTimezone
        ? t(makeKey(selectDateWithTimezone), selectDateWithTimezone)
        : '',
      icon: 'clock',
    });
  }

  if (
    state.matches('bookingForm') &&
    state.context.selectedDate &&
    state.context.selectedTimeSlot
  ) {
    const selectDateWithTimezone = new TZDate(
      `${state.context.selectedDate}T00:00:00`,
    );

    bookingInfoItems.push({
      label: format(selectDateWithTimezone, 'PP', {language}),
      icon: 'calendar',
    });

    const startTimeDate = parse(
      state.context.selectedTimeSlot?.start_time,
      'HH:mm',
      new Date(),
    );

    const endTimeDate = parse(
      state.context.selectedTimeSlot?.end_time,
      'HH:mm',
      new Date(),
    );

    const timezone =
      timezoneName(`${state.context.selectedDate}T00:00:00`, language) || '';

    bookingInfoItems.push({
      label: `${format(startTimeDate, 'p', {language})} - ${format(
        endTimeDate,
        'p',
        {language},
      )}`,
      subLabel: timezone,
      icon: 'clock',
    });
  }

  state.context.eventTemplate?.location_meta.forEach((value) => {
    const {label, icon} = locationTitleLabelMap[value.type];
    bookingInfoItems.push({
      label: meetLinkProviders[value.meta.provider] || label || '',
      icon,
    });
  });

  return {
    title:
      state.context.eventTemplate?.name ||
      state.context.eventDetails?.title ||
      '',
    bookingInfoItems,
  };
};

export const selectBookingFormInputs = (
  state: IBookingMachineSnapshot,
  t: TranslationFunction,
): IInputItem[] => {
  const additionalQuestions =
    state.context.eventTemplate?.additional_questions?.map(
      (question, index): IInputItem => {
        return {
          name: `additionalQuestion-${index}`,
          label: question.question,
          type: question.answer_type,
          options: question.choices,
        };
      },
    ) || [];

  return [
    {
      name: 'guestName',
      label: t('Full_Name', 'Full Name'),
      isRequired: true,
      placeholder: t('Enter_full_name', 'Enter full name'),
      type: 'text',
    },
    {
      name: 'guestEmail',
      label: t('Email', 'Email'),
      isRequired: true,
      placeholder: t('Enter_email', 'Enter email'),
      type: 'email',
    },
    {
      name: 'guestPhone',
      label: t('Phone_Number', 'Phone Number'),
      isRequired: true,
      placeholder: t('Enter_phone_number', 'Enter phone number'),
      type: 'phone',
    },
    ...additionalQuestions,
  ];
};

export const selectAdditionalQuestions = (
  state: IBookingMachineSnapshot,
): IEventQuestion[] => {
  return state.context.eventTemplate?.additional_questions || [];
};

// passing state here for future use
export const selectBookingNotifyHostInputs = (
  state: IBookingMachineSnapshot,
  t: TranslationFunction,
): IInputItem[] => {
  return [
    {
      name: 'guestName',
      label: t('Full_Name', 'Full Name'),
      isRequired: true,
      placeholder: t('Enter_full_name', 'Enter full name'),
      type: 'text',
    },
    {
      name: 'guestEmail',
      label: t('Email', 'Email'),
      isRequired: true,
      placeholder: t('Enter_email', 'Enter email'),
      type: 'email',
    },
    {
      name: 'guestPhone',
      label: t('Phone_Number', 'Phone Number'),
      isRequired: true,
      placeholder: t('Enter_phone_number', 'Enter phone number'),
      type: 'phone',
    },
  ];
};

export const selectCurrentDateSlots = (state: IBookingMachineSnapshot) => {
  if (state.context.selectedDate) {
    return (
      state.context.timeSlots?.free_time_slots?.[state.context.selectedDate] ||
      []
    );
  }
  return [];
};

export const selectSelectedTimeSlot = (state: IBookingMachineSnapshot) => {
  return state.context.selectedTimeSlot;
};

export const selectCanNavigatePrevious = (
  state: IBookingMachineSnapshot,
): boolean => {
  const {selectedStartDateOfWeek, availabilityRange} = state.context;

  if (!selectedStartDateOfWeek || !availabilityRange) {
    throw new Error(
      'Selected start date of week or availability range is not found',
    );
  }

  if (state.matches({dateTimePicker: 'availabilityLoading'})) {
    return false;
  }
  const timezone = state.context.timezone;

  return canNavigatePreviousWeek(
    timezone,
    selectedStartDateOfWeek,
    availabilityRange,
  );
};

export const selectCanNavigateNext = (
  state: IBookingMachineSnapshot,
): boolean => {
  const {selectedStartDateOfWeek, availabilityRange, timeSlots} = state.context;

  if (!selectedStartDateOfWeek || !availabilityRange) {
    throw new Error(
      'Selected start date of week or availability range is not found',
    );
  }

  if (state.matches({dateTimePicker: 'availabilityLoading'})) {
    return false;
  }

  const isTimeSlotsAvailableTillCurrentWeek =
    Object.keys(timeSlots?.free_time_slots || {}).length > 0;
  const timezone = state.context.timezone;

  return canNavigateNextWeek(
    timezone,
    selectedStartDateOfWeek,
    availabilityRange,
    isTimeSlotsAvailableTillCurrentWeek,
  );
};

export const selectSelectedDate = (
  state: IBookingMachineSnapshot,
  language: string,
) => {
  const {selectedDate} = state.context;
  if (!selectedDate) {
    return undefined;
  }

  return {
    key: selectedDate,
    // TODO: need to fix this
    label: selectedDate,
    // secondaryLabel: format(selectedDate, 'E', {language}),
  };
};

export const selectWeekDays = (
  state: IBookingMachineSnapshot,
  language: string,
) => {
  const {selectedStartDateOfWeek, timeSlots} = state.context;

  if (!selectedStartDateOfWeek) {
    throw new Error('Selected start date of week is not found');
  }
  const weekDates = getWeekDates(selectedStartDateOfWeek);
  const weekDayOptions = weekDates.map((date) => ({
    key: formatISO(date, {representation: 'date'}),
    label: format(date, 'd', {language}),
    secondaryLabel: format(date, 'E', {language}),
  }));

  const allTimeSlotsKeys = Object.keys(timeSlots?.free_time_slots || {});
  const disabledWeekDays = weekDayOptions
    .filter((day) => !allTimeSlotsKeys.includes(day.key))
    .map((day) => day.key);

  return {
    weekDayOptions,
    disabledWeekDays,
  };
};

export const selectWeekRangeDisplay: (
  state: IBookingMachineSnapshot,
  language: string,
) => string = (state, language) => {
  const {selectedStartDateOfWeek} = state.context;

  if (!selectedStartDateOfWeek) {
    throw new Error('Selected start date of week is not found');
  }
  const weekStartDate = selectedStartDateOfWeek;
  const weekEndDate = addDays(weekStartDate, 6);

  return `${format(weekStartDate, 'PP', {
    language,
  })} - ${format(weekEndDate, 'PP', {language})}`;
};
